/**
 * A minimal Office Open XML (.xlsx) writer and reader. Covers what a saved
 * scenario needs, plain text and number cells with a bold header style, and
 * reads back files Excel has re-saved, which store text as shared strings.
 */
import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";

export type CellValue = string | number | boolean | null;

export interface SheetRow {
  cells: CellValue[];
  bold?: boolean;
}

export interface Sheet {
  name: string;
  rows: SheetRow[];
  /** Column widths in characters, from column A. */
  widths?: number[];
}

export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const MAIN_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const DOC_REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const PKG_REL_NS = "http://schemas.openxmlformats.org/package/2006/relationships";
const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';

const STYLES =
  `<styleSheet xmlns="${MAIN_NS}">` +
  '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>' +
  '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
  '<fills count="2"><fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  "</styleSheet>";

/**
 * UTF-8 bytes as this realm's Uint8Array. fflate tells files from folders with
 * instanceof, and TextEncoder can return another realm's array, as under jsdom.
 */
export function utf8(text: string): Uint8Array {
  return new Uint8Array(strToU8(text));
}

function escapeXml(text: string): string {
  return text
    // Characters XML 1.0 cannot carry at all, even escaped.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function columnName(index: number): string {
  let name = "";
  let rest = index + 1;
  while (rest > 0) {
    const remainder = (rest - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    rest = Math.floor((rest - 1) / 26);
  }
  return name;
}

function columnIndex(reference: string): number {
  const letters = /^[A-Z]+/i.exec(reference)?.[0].toUpperCase() ?? "A";
  let index = 0;
  for (const letter of letters) index = index * 26 + (letter.charCodeAt(0) - 64);
  return index - 1;
}

/** Excel limits sheet names to 31 characters and forbids a few symbols. */
function sheetName(name: string): string {
  return name.replace(/[[\]:*?/\\]/g, " ").slice(0, 31) || "Sheet";
}

function cellXml(value: CellValue, reference: string, bold: boolean): string {
  const style = bold ? ' s="1"' : "";
  if (value == null || value === "") return "";
  if (typeof value === "number") {
    return Number.isFinite(value) ? `<c r="${reference}"${style}><v>${value}</v></c>` : "";
  }
  if (typeof value === "boolean") {
    return `<c r="${reference}"${style} t="b"><v>${value ? 1 : 0}</v></c>`;
  }
  return (
    `<c r="${reference}"${style} t="inlineStr">` +
    `<is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`
  );
}

function sheetXml(sheet: Sheet): string {
  const cols = sheet.widths?.length
    ? `<cols>${sheet.widths
        .map(
          (width, index) =>
            `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`,
        )
        .join("")}</cols>`
    : "";
  const rows = sheet.rows
    .map((row, rowIndex) => {
      const cells = row.cells
        .map((value, columnIndexValue) =>
          cellXml(value, `${columnName(columnIndexValue)}${rowIndex + 1}`, Boolean(row.bold)),
        )
        .join("");
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");
  return `${XML_HEAD}<worksheet xmlns="${MAIN_NS}">${cols}<sheetData>${rows}</sheetData></worksheet>`;
}

/** Builds an .xlsx file from the given sheets. */
export function writeWorkbook(sheets: Sheet[]): Uint8Array {
  const names = sheets.map((sheet) => escapeXml(sheetName(sheet.name)));
  const overrides = sheets
    .map(
      (_, index) =>
        `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ` +
        'ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>',
    )
    .join("");
  const files: Record<string, Uint8Array> = {
    "[Content_Types].xml": utf8(
      `${XML_HEAD}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        `${overrides}</Types>`,
    ),
    "_rels/.rels": utf8(
      `${XML_HEAD}<Relationships xmlns="${PKG_REL_NS}">` +
        `<Relationship Id="rId1" Type="${DOC_REL_NS}/officeDocument" Target="xl/workbook.xml"/>` +
        "</Relationships>",
    ),
    "xl/workbook.xml": utf8(
      `${XML_HEAD}<workbook xmlns="${MAIN_NS}" xmlns:r="${DOC_REL_NS}"><sheets>` +
        names
          .map(
            (name, index) =>
              `<sheet name="${name}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`,
          )
          .join("") +
        "</sheets></workbook>",
    ),
    "xl/_rels/workbook.xml.rels": utf8(
      `${XML_HEAD}<Relationships xmlns="${PKG_REL_NS}">` +
        sheets
          .map(
            (_, index) =>
              `<Relationship Id="rId${index + 1}" Type="${DOC_REL_NS}/worksheet" ` +
              `Target="worksheets/sheet${index + 1}.xml"/>`,
          )
          .join("") +
        `<Relationship Id="rId${sheets.length + 1}" Type="${DOC_REL_NS}/styles" Target="styles.xml"/>` +
        "</Relationships>",
    ),
    "xl/styles.xml": utf8(XML_HEAD + STYLES),
  };
  sheets.forEach((sheet, index) => {
    files[`xl/worksheets/sheet${index + 1}.xml`] = utf8(sheetXml(sheet));
  });
  return zipSync(files, { level: 6 });
}

export class WorkbookReadError extends Error {}

function parseXml(files: Record<string, Uint8Array>, path: string): Document | null {
  const bytes = files[path];
  if (!bytes) return null;
  const document = new DOMParser().parseFromString(strFromU8(bytes), "application/xml");
  if (document.getElementsByTagName("parsererror").length > 0) {
    throw new WorkbookReadError(`${path} is not valid XML.`);
  }
  return document;
}

function elements(parent: Document | Element, localName: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS("*", localName));
}

function children(parent: Element, localName: string): Element[] {
  return Array.from(parent.children).filter((child) => child.localName === localName);
}

/** Text of a string item, joining rich-text runs and skipping phonetic hints. */
function stringItemText(item: Element): string {
  return elements(item, "t")
    .filter((node) => node.parentElement?.localName !== "rPh")
    .map((node) => node.textContent ?? "")
    .join("");
}

function resolveTarget(target: string): string {
  if (target.startsWith("/")) return target.slice(1);
  const parts = `xl/${target}`.split("/");
  const resolved: string[] = [];
  for (const part of parts) {
    if (part === "..") resolved.pop();
    else if (part !== ".") resolved.push(part);
  }
  return resolved.join("/");
}

/** Reads every sheet of an .xlsx file as rows of cell values, by sheet name. */
export function readWorkbook(bytes: Uint8Array): Map<string, CellValue[][]> {
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(bytes);
  } catch {
    throw new WorkbookReadError("The file is not an Excel workbook.");
  }
  const workbook = parseXml(files, "xl/workbook.xml");
  if (!workbook) throw new WorkbookReadError("The file is not an Excel workbook.");

  const targets = new Map<string, string>();
  let sharedStringsPath = "xl/sharedStrings.xml";
  const relationships = parseXml(files, "xl/_rels/workbook.xml.rels");
  if (relationships) {
    for (const relation of elements(relationships, "Relationship")) {
      const target = resolveTarget(relation.getAttribute("Target") ?? "");
      targets.set(relation.getAttribute("Id") ?? "", target);
      if (relation.getAttribute("Type")?.endsWith("/sharedStrings")) {
        sharedStringsPath = target;
      }
    }
  }

  const sharedStringsDocument = parseXml(files, sharedStringsPath);
  const sharedStrings = sharedStringsDocument
    ? elements(sharedStringsDocument, "si").map(stringItemText)
    : [];

  const sheets = new Map<string, CellValue[][]>();
  elements(workbook, "sheet").forEach((sheet, index) => {
    const relationId =
      sheet.getAttributeNS(DOC_REL_NS, "id") ?? sheet.getAttribute("r:id") ?? "";
    const path = targets.get(relationId) ?? `xl/worksheets/sheet${index + 1}.xml`;
    const document = parseXml(files, path);
    if (!document) return;
    const rows: CellValue[][] = [];
    elements(document, "row").forEach((row, position) => {
      const rowNumber = Number(row.getAttribute("r")) || position + 1;
      const values: CellValue[] = [];
      children(row, "c").forEach((cell, cellPosition) => {
        const reference = cell.getAttribute("r");
        const column = reference ? columnIndex(reference) : cellPosition;
        const type = cell.getAttribute("t");
        const raw = children(cell, "v")[0]?.textContent ?? null;
        let value: CellValue = null;
        if (type === "inlineStr") {
          const inline = children(cell, "is")[0];
          value = inline ? stringItemText(inline) : "";
        } else if (type === "s") {
          value = raw == null ? null : (sharedStrings[Number(raw)] ?? null);
        } else if (type === "str") {
          value = raw;
        } else if (type === "b") {
          value = raw === "1";
        } else if (type !== "e" && raw != null && raw !== "") {
          const number = Number(raw);
          value = Number.isFinite(number) ? number : raw;
        }
        values[column] = value;
      });
      rows[rowNumber - 1] = Array.from(values, (value) => value ?? null);
    });
    sheets.set(
      sheet.getAttribute("name") ?? `Sheet${index + 1}`,
      Array.from(rows, (row) => row ?? []),
    );
  });
  return sheets;
}
